/************************************** THIRD-PARTY MODULES ***************************************/
import moment from 'moment';
import 'reflect-metadata';

const {defineProperty, set, defineMetadata, getMetadata, hasMetadata, deleteMetadata} = Reflect;

/**************************************** PROJECT MODULES *****************************************/
import {serializationConfig, StoreSerialiationConfig} from './serialization-config';

const {
    SERIALIZER,
    DESERIALIZER,
    STORED_PROPS,
    REFERENCE_PROP,
    PROPERTY_TYPE,
    CUR_INSTANCE_IDX,
    TOTAL_INSTANCES,
    KEEP_ON_LOGOUT,
    VAL_AFTER_LOGOUT,
    REF_PROP_VALUES,
} = serializationConfig.symbols;

/********************************************* EXPORT *********************************************/
/**
 * Define a hidden property that can be written to (but not deleted)
 *
 * @param {object} obj Object to add property to
 * @param {string|Symbol} prop Property to add new value to
 * @return {boolean} true if property setting succeeded
 */
export function defHiddenWriteableProp<V = any>(
    obj: object,
    key: string | Symbol,
    val: V
): boolean {
    return defineProperty(obj, key as any, {enumerable: false, writable: true, value: val});
}

/**
 * Set values at referenceProp for all instances of given class.
 *
 * @param {object} instance Get parent class from this instance, then get array of
 *                          reference prop values of for all instances of the class.
 * @return {T[]} Values of reference prop
 */
export function addInstanceRefPropValToRefPropValList<T = string>(
    instance: object,
    refPropVal: string
): RefPropObj[] | void {
    return defineMetadata(
        REF_PROP_VALUES,
        (getRefPropObjects(instance) || []).concat({refPropVal: refPropVal, instance}),
        instance.constructor
    );
}

/**
 * Build key that will be used to store the data for this particular instance.
 *
 * @example return val ::  main [RecordModel<35f32db3-2751-4697-a96b-760984a547c8>] fullName
 *                         \  /  \         / \                                  /    \     /
 *                 storePrefix    className   \--- instance[referenceProp] ----/      -key-
 *
 * @param {string} storePrefix String to place before the rest of the key (pre-class name)
 * @param {string} className Name of class the db storage key is being built for
 * @param {Object} instance Class to build key from (for getting needed prop val)
 * @param {string} referenceProp Property (on instance class) to get the reference value from
 * @param {string} key Current data being stored
 *
 * @return {string} db storage key to store data at in IndexedDb (or localStorage)
 */
export function buildDbStorageKey(
    instance: Object,
    key: string,
    storePrefix: string = '',
    referenceVal: string | void = null
): string {
    // Build prefix and value of reference prop [to insert in <>]
    const prefix = storePrefix ? storePrefix + '' : '';
    const reference = referenceVal || getReferenceVal(instance);

    // Construct final key
    const className = getClassName(instance);
    const dbStorageKey = `[${prefix}${className}${reference ? `<${reference}>` : ''}] ${key}`;

    // Log & return newly created storage key
    return dbStorageKey;
}

/************************************ TYPE & STATUS DETECTION *************************************/
/**
 *  Determine if given instance's class defines the given key as a property of type 'object'
 *  @param {object} instance Determine if given key on this object is an object prop
 *  @param {string} key Check if this is defined as an object of some sort
 *  @return {boolean} true if instance's class defines given key as prop of type 'object'
 */
export function isPropTypeObject(instance: object & {prototype?: object}, key: string): boolean {
    /*** Check data type at property if there's a value assigned ***/
    if (instance[key] !== null && typeof instance[key] === 'object') return true;

    /*** Try to determine if propType is set in the metadata if no value assigned ***/
    if (!instance.prototype) return false;

    const keyType = Reflect.getMetadata('design:type', instance.prototype, key);
    return keyType && keyType.name === 'Object';
}

/**
 * @return {boolean} Returns true if {instance}.{instanceProp} is a Moment object, or will be
 *                   once instantiation is complete.
 */
export const isMomentProp = <T extends object>(instance: T, key: string): boolean => {
    return !!(
        instance[key] instanceof moment ||
        (instance[key] &&
            Object.getPrototypeOf(instance[key]).constructor.name.toLowerCase() === 'moment') ||
        // HACK :: try to find key's signature in constructor's src code
        ((isPropTypeObject(instance, key) &&
            instance.toString().match(new RegExp(`  _this\\.${key} = moment\\(`))) ||
            instance.constructor.toString().match(new RegExp(`  _this\\.${key} = moment\\(`)))
    );
};

/**
 * Determine if given prop should be preserved (not deleted from memory & storage) past logout
 * @param {object} instance Get prop from this class instance's constructor
 * @return {boolean} True if given prop should not be erased on logout
 */
export const doKeepOnLogout = <T extends object>(instance: T, protoKey: string): string =>
    getMetadata(KEEP_ON_LOGOUT, instance.constructor, protoKey);

/**
 * Determine if given prop should be preserved (not deleted from memory & storage) past logout
 * @param {object} instance Get prop from this class instance's constructor
 * @return {boolean} True if given prop should not be erased on logout
 */
export const revertToValOnLogout = <T extends object, R>(instance: T, protoKey: string): R => {
    const val = getMetadata(VAL_AFTER_LOGOUT, instance.constructor, protoKey);
    if (typeof val === `undefined`) return undefined;
    return val;
};

/**
 * If given instance has been initialized for storage, return true; otherwise warn & return false.
 *
 * @param {object} klassProto Class to check readiness of (for storage)
 * @return {boolean} If true, object has been deemed ready for storage (i.e. HAS_REF_PROP present)
 */
export function isInitForStorage<T extends object>(instance: T, isSingleton: boolean): boolean {
    if (getReferenceProp(instance) || isSingleton) return true;

    console.warn(
        `Cannot initialize serialization on ${instance.constructor.name} - no storage `,
        `decorators (e.g. referenceProp, storeLocally) have been attached to it`
    );

    return false;
}

/********************************* EXTRACT METADATA FROM CLASSES **********************************/
/**
 * Get object containing keys of all serializable properties from the given Class' prototype.
 *
 * @param {object} instance Class instance to get serializable properties list for
 * @return {string[]} keys in storageConfig's propsStored list + reference prop key (if present)
 */
export function getStoredPropsList(instance: object): string[] {
    // Get keys of interest from given class' storage config object
    const propsStored = getMetadata(STORED_PROPS, instance.constructor);
    const referenceProp = getReferenceProp(instance);

    return (propsStored || []).concat(referenceProp || []);
}

export type RefPropObj = {refPropVal: string; instance: object};

/**
 * Get values at referenceProp for all instances of given class.
 *
 * @param {object} instance Get parent class from this instance, then get array of
 *                          reference prop values for all instances of the class.
 * @return {T[]} Values of reference prop
 */
export function getRefPropObjects<T = string>(instance: object): RefPropObj[] | void {
    return Reflect.getMetadata(REF_PROP_VALUES, instance.constructor);
}

/**
 * Get the reference property from the given class / prototype object. This is used for storing
 * more than one instance of a store class in localForage (IndexedDB).
 *
 * @param {object} instance Class to get reference property from
 * @return {string} referenceProp name (e.g. 'fullName') - NOT the data it contains
 */
export const getReferenceProp = <T extends object>(instance: T): string =>
    getMetadata(REFERENCE_PROP, instance.constructor);

/**
 * Get the value of the data at the reference property of the given class.
 *
 * @param {object} instance Class to get data corresponding to its reference property from
 * @return {string} data contained in referenceProp (e.g. Meeka), NOT the key name (e.g. fullName)
 */
export const getReferenceVal = <T extends object>(instance: T): string =>
    instance[getReferenceProp(instance)];

/**
 * Get the name of the class / prototype that instantiated the given instance.
 * @param {object} instance Instance to get name of parent class for
 * @return {string} parent class name
 */
export const getClassName = <T extends object>(instance: T): string => instance.constructor.name;

/**
 * Get the name of the given class / prototype object.
 * @param {object} instance Class to get name of
 * @return {string} class name
 */
export const getInstanceProto = <T extends object, R extends object = T>(instance: T): R =>
    instance.constructor.prototype;

/**
 * Get all "artificially" added class data.
 *
 * @param {object} instance Class instance to get data from (most props will be on constructor)
 * @return {object} {className, referenceProp, referenceVal, serializableProps, storageConfig}
 */
export const getClassData = <T extends object>(instance: T) => ({
    className: getClassName(instance),
    referenceProp: getReferenceProp(instance),
    referenceVal: getReferenceVal(instance),
    serializableProps: getStoredPropsList(instance),
    proto: getInstanceProto(instance),
});

/************************ SERIALIZATION FUNCTION: GET & SET DATA ON PROPS *************************/
/**
 * Set serializer function for converting instance to storable data when writing to store
 *
 * @param {object} instance Set store serializer on class of this instance (at given instanceKey)
 * @param {string} instanceKey Store serializer on this prop of the isntance's class
 * @param {Function} serializer Function for converting instance to storable data
 */
export function setSerializer<T = string>(
    instance: object,
    instanceKey: string,
    serializer: (o) => T
): void {
    Reflect.defineMetadata(SERIALIZER, serializer, instance.constructor, instanceKey);
}

/**
 * Get serializer function for converting instance to storable data when writing to store.
 *
 * @param {object} instance Get store serializer from class of this instance (at given instanceKey)
 * @param {string} instanceKey Store serializer on this prop of the instance's class
 * @return {Function} serializer Function for converting instance to storable data
 */
export function getSerializer<T = string>(
    instance: object,
    instanceKey: string
): (<T = O, O = any>(o: O) => T) | void {
    return Reflect.getMetadata(SERIALIZER, instance.constructor, instanceKey);
}

/**
 * Set deserializer function for converting data back to instance when retrieving from store.
 *
 * @param {object} instance Set store deserializer on class of this instance (at instanceKey)
 * @param {string} instanceKey Store deserializer on this prop of the isntance's class
 * @param {Function} deserializer Function for converting stored data back to instance
 */
export function setDeserializer<T extends object = object>(
    instance: object,
    instanceKey: string,
    deserializer: (o) => T
): void {
    Reflect.defineMetadata(DESERIALIZER, deserializer, instance.constructor, instanceKey);
}

/**
 * Get deserializer function for reinstantiating stored data on retrieval from store.
 *
 * @param {object} instance Get store deserializer from class of this instance (at instanceKey)
 * @param {string} instanceKey Store deserializer on this prop of the instance's class
 * @return {Function} deserializer Function for converting stored data back to instance
 */
export function getDeserializer<T = string>(
    instance: object,
    instanceKey: string
): (<T = O, O = any>(o: O) => T) | void {
    return Reflect.getMetadata(DESERIALIZER, instance.constructor, instanceKey);
}

/******************************************** COUNTERS ********************************************/
/**
 * [MUTATOR] Increment given prototype's instance counter metaproperty Symbol("TOTAL_INSTANCES")
 *
 * Create & set to 0 if not present
 * Counter tracks the number of instances of the given prototype
 * Also gives the object itself an instance number
 *
 * @param {object} instance Increment instance count on this object's class, and give
 *                          the object itself a current instance number
 * @param {boolean} isSingleton Set TOTAL_INSTANCES val to 1 if true (only 1 singleton can exist)
 */
export function incrementClassInstanceCounter(instance: object, isSingleton: boolean): void {
    // Determine number of instances of this instance's class (including this instance)
    const totalInstances = hasMetadata(TOTAL_INSTANCES, instance.constructor)
        ? getMetadata(TOTAL_INSTANCES, instance.constructor) + 1
        : 1;

    // Store number of instance of class on class itself
    defineMetadata(TOTAL_INSTANCES, totalInstances, instance.constructor);

    // Store current "instances number" on actual instance if not present.
    defineMetadata(CUR_INSTANCE_IDX, getMetadata(TOTAL_INSTANCES, instance.constructor), instance);
}

/**
 * Increment metadata counter (on instance) by 1
 * If no metadata at given symbol, create it & set value to 0
 *
 * @param {object} klass Class to store property on
 * @param {Symbol} metadataKey Metadata key to use to store data on klass constructor.
 */
export function incrementMetadataCounter(metadataKey: Symbol, klass: object) {
    return Reflect.defineMetadata(
        metadataKey,
        (Reflect.getMetadata(metadataKey, klass) || 0) + 1,
        klass
    );
}

/**
 * Decrement metadata counter (on klass) by 1
 * If no metadata at given symbol, create it & set value to 0
 *
 * @param {object} klass Class to store property on
 * @param {Symbol} metadataKey Metadata key to use to store data on klass constructor.
 */
export function decrementMetadataCounter(metadataKey: Symbol, klass: object) {
    return Reflect.defineMetadata(
        metadataKey,
        (Reflect.getMetadata(metadataKey, klass) || 0) + 1,
        klass
    );
}

/**************************************** OBJECT UTILITIES ****************************************/
/**
 * Place instance of original constructor's prototype to new constructor's prototype slot.
 * Place original constructor in new constructor's prototype.constructor slot.
 *
 * @param {object & Function} originalConstructor Original constructor function
 * @param {object & Function} newConstructor New constructor function
 *
 * @return {object} New constructor augmented by original constructor & instance of its prototype
 */
export function augmentNewConstructorWithOrig<TFunc>(
    originalConstructor: {prototype: object & {constructor: object}},
    newConstructor: (...args: any[]) => void
) {
    newConstructor.prototype = Object.create(originalConstructor.prototype);
    newConstructor.prototype.constructor = originalConstructor;
    // Hoist static methods/properties
    Object.keys(originalConstructor).forEach(
        key => (newConstructor[key] = originalConstructor[key])
    );
    return (newConstructor as any) as TFunc;
}
